import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppStore } from '../stores/appStore'
import singleBed from '../../static/images/single-bed.png'
import doubleBed from '../../static/images/double-bed.png'
import { cn } from '@/lib/utils'

interface BedVariationModel {
  id: number
  width_cm: number
  seamless?: boolean
  three_d_model: {
    id: number
    file_url: string
    preview_url: string | null
  }
}

type BedType = 'single' | 'double' | null

interface BedTypeStepProps {
  hasSingle: boolean
  hasDouble: boolean
  bedType: BedType
  onSelectType: (type: Exclude<BedType, null>) => void
}

const BedTypeStep = ({ hasSingle, hasDouble, bedType, onSelectType }: BedTypeStepProps) => {
  const { t } = useTranslation()

  return (
    <div className="mb-12">
      <h2 className="text-[28px] font-normal text-[#666] mb-6 text-center">
        {t('common.what-bed-type')}
      </h2>
      <div className="flex gap-6 justify-center">
        {hasSingle && (
          <button
            type="button"
            onClick={() => onSelectType('single')}
            className="text-center"
          >
            <img 
              src={singleBed} 
              alt="Enkelsäng" 
              className={`w-32 h-32 object-cover  mb-2 ${bedType === 'single' ? 'outline-selected' : ''}`}
            />
            <span className="text-sm text-gray-800">
              {t('common.single')}
            </span>
          </button>
        )}
        {hasDouble && (
          <button
            type="button"
            onClick={() => onSelectType('double')}
            className="text-center"
          >
            <img 
              src={doubleBed} 
              alt="Dubbelsäng" 
              className={`w-32 h-32 object-cover mb-2 ${bedType === 'double' ? 'outline-selected' : ''}`}
            />
            <span className="text-sm text-gray-800 font-medium">
              {t('common.double')}
            </span>
          </button>
        )}
      </div>
    </div>
  )
}

interface SizeStepProps {
  bedVariation: { models: BedVariationModel[] }
  tempSelectedSize: number | null
  isSizeEnabled: (width: number) => boolean
  onSelectSize: (width: number) => void
  bedType: BedType
}

const SizeStep = ({ bedVariation, tempSelectedSize, isSizeEnabled, onSelectSize, bedType }: SizeStepProps) => {
  const { t } = useTranslation()
  
  return (
    <div className={cn('transition-all duration-300 overflow-hidden', bedType ? 'h-auto': 'h-0')}>
      <hr className='my-4'/>
      <h2 className="justify-center mb-2 text-gray-900 text-sm font-medium font-['Suisse_Int\'l'] leading-[14px]">
        {t('bed.size')}
      </h2>
      
      <div className={cn("grid grid-cols-3 gap-3")}>
        {Array.from(new Map(bedVariation.models
          .sort((a: BedVariationModel, b: BedVariationModel) => a.width_cm - b.width_cm || Number(a.seamless) - Number(b.seamless))
          .map((m: BedVariationModel) => [m.width_cm, m])
        ).values()).map((model: BedVariationModel) => (
          <button
            key={model.id}
            disabled={!isSizeEnabled(model.width_cm)}
            onClick={() => isSizeEnabled(model.width_cm) && onSelectSize(model.width_cm)}
            className={`h-10 px-6 py-[9px] bg-white inline-flex justify-center items-center gap-2.5 transition-colors outline outline-offset-[-1px] ${
              tempSelectedSize === model.width_cm
                ? 'outline-neutral-700 !bg-neutral-700 text-white'
                : isSizeEnabled(model.width_cm)
                  ? 'outline-zinc-400 outline-2 text-zinc-600 hover:outline-zinc-600'
                  : 'outline-zinc-200 outline-1 text-zinc-200 cursor-not-allowed'
            }`}
          >
            <div className="justify-center text-base font-normal font-['Suisse_Int\'l'] leading-[14px]">
              {model.width_cm} cm
            </div>
          </button>
        ))}
      </div>
    </div>
  )
}

// Size Selector Component - redesigned to match the reference design
const SizeSelector = () => {
  const { t } = useTranslation()
  const { bedConfig, selectSize, isLoadingBedVariation } = useAppStore()
  const { variation: bedVariation } = bedConfig.bed
  const [tempSelectedSize, setTempSelectedSize] = useState<number | null>(null)
  const [bedType, setBedType] = useState<BedType>(null)

  const isSizeEnabled = (width: number): boolean => {
    if (bedType === null) return false
    if (bedType === 'single') return width < 150
    return width >= 150
  }

  // Find what types are available based on model widths, without useEffect or extra state
  const widths = bedVariation?.models.map((m: BedVariationModel) => m.width_cm) ?? []
  const hasSingle = widths.some((w) => w < 150)
  const hasDouble = widths.some((w) => w >= 150)

  // Set initial bedType if only one available, else allow selection
  useEffect(() => {
    if (bedType === null) {
      if (hasSingle && !hasDouble) setBedType('single')
      else if (hasDouble && !hasSingle) setBedType('double')
    }
    // eslint-disable-next-line
  }, [hasSingle, hasDouble])

  if (isLoadingBedVariation) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="p-4 text-center">
          <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
          <p className="mt-2 text-sm text-gray-600">{t('loading')}</p>
        </div>
      </div>
    )
  }

  if (!bedVariation || bedVariation.models.length === 0) {
    // Return null to avoid rendering anything
    // TODO: Come back to this and add proper handling.
    return null
  }

  const handleStartBuild = () => {
    if (tempSelectedSize !== null) {
      // Use selectSize action which also loads the corresponding model
      selectSize(tempSelectedSize)
    }
  }

  const handleSelectBedType = (type: Exclude<BedType, null>) => {
    setBedType(type)
    setTempSelectedSize(null)
  }

  const handleSelectSize = (width: number) => {
    setTempSelectedSize(width)
  }

  return (
    <div className="min-h-screen flex items-center">
      <div className="w-full max-w-lg mx-auto p-8 h-fit">
        <BedTypeStep
          hasSingle={hasSingle}
          hasDouble={hasDouble}
          bedType={bedType}
          onSelectType={handleSelectBedType}
        />

        <SizeStep
          bedType={bedType}
          bedVariation={bedVariation}
          tempSelectedSize={tempSelectedSize}
          isSizeEnabled={isSizeEnabled}
          onSelectSize={handleSelectSize}
        />
        <div className={cn('transition-all duration-300 overflow-hidden', tempSelectedSize ? 'h-auto': 'h-0')}>
        <hr className='my-4'/>

        {/* Start Build Button */}
        <button
          onClick={handleStartBuild}
          disabled={tempSelectedSize === null}
          className={`text-white text-sm font-light font-['Suisse_Int'l']uppercase h-10  bg-neutral-700 w-full ${
            tempSelectedSize !== null
              ? ' opacity-100'
              : '  opacity-30 cursor-not-allowed'
          }`}
        >
          {t('common.start-build')}
        </button>
        </div>
      </div>
    </div>
  )
}

export default SizeSelector

